import { baseApi } from "../api/baseApi";

function ynToBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value.trim().toUpperCase() === "Y";
  }
  return Boolean(value);
}

function normalizeClientInvoiceListItem(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  return {
    invoiceId: row.invoiceId ?? row.invoice_id,
    invoiceNumber: String(row.invoiceNumber ?? row.invoice_number ?? "").trim(),
    custgroup: String(row.custgroup ?? "").trim(),
    clientName: String(row.clientName ?? row.client_name ?? "").trim(),
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
    invoiceAmount: row.invoiceAmount ?? row.invoice_amount ?? null,
    invoiceDate: row.invoiceDate ?? row.invoice_date ?? null,
    serviceMonth: String(row.serviceMonth ?? row.service_month ?? "").trim(),
    status: String(row.status ?? "").trim(),
    invoiceProgramName: String(row.invoiceProgramName ?? row.invoice_program_name ?? "").trim(),
  };
}

function normalizeTradeshowNames(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((name) => String(name ?? "").trim())
    .filter(Boolean);
}

function normalizeClientInvoiceDraftLine(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  return {
    invoiceLineId: row.invoiceLineId ?? row.invoice_line_id,
    invProfileDetailId: row.invProfileDetailId ?? row.inv_profile_detail_id ?? null,
    invoicePcrId: normalizeOptionalNumber(row.invoicePcrId ?? row.invoice_pcr_id),
    invoicePostageId: normalizeOptionalNumber(row.invoicePostageId ?? row.invoice_postage_id),
    invoiceFreightId: normalizeOptionalNumber(row.invoiceFreightId ?? row.invoice_freight_id),
    invoicePodId: normalizeOptionalNumber(row.invoicePodId ?? row.invoice_pod_id),
    defaultInvoiceDescription: String(
      row.defaultInvoiceDescription ?? row.default_invoice_description ?? ""
    ).trim(),
    quantity: row.quantity ?? null,
    rate: row.rate != null ? Number(row.rate) : null,
    amount: row.amount != null ? Number(row.amount) : null,
    adjustment: row.adjustment != null ? Number(row.adjustment) : null,
    suppressed: ynToBoolean(row.suppressed),
    overrideFlag: ynToBoolean(row.overrideFlag ?? row.overrides),
    overrideAmount:
      ynToBoolean(row.overrideFlag ?? row.overrides)
        ? row.overrideAmount != null
          ? Number(row.overrideAmount)
          : row.override_amount != null
            ? Number(row.override_amount)
            : null
        : null,
    overrideReason: String(row.overrideReason ?? row.override_reason ?? "").trim(),
    overrideSetBy: row.overrideSetBy ?? row.override_set_by ?? null,
    overrideSetOn: row.overrideSetOn ?? row.override_set_on ?? null,
    overrideQuantity:
      row.overrideQuantity != null
        ? Number(row.overrideQuantity)
        : row.override_quantity != null
          ? Number(row.override_quantity)
          : null,
    overrideQuantityReason: String(
      row.overrideQuantityReason ?? row.override_quantity_reason ?? ""
    ).trim(),
    overrideQuantitySetBy: row.overrideQuantitySetBy ?? row.override_quantity_set_by ?? null,
    overrideQuantitySetOn:
      row.overrideQuantitySetOn ?? row.override_quantity_set_on ?? null,
    tradeshowNames: normalizeTradeshowNames(row.tradeshowNames ?? row.tradeshow_names),
  };
}

function normalizeClientInvoiceDraftSaveResponse(row) {
  if (!row || typeof row !== "object") {
    return row;
  }
  const lines = Array.isArray(row.lines)
    ? row.lines.map(normalizeClientInvoiceDraftLine).filter(Boolean)
    : [];
  return {
    invoiceId: row.invoiceId ?? row.invoice_id,
    invoiceNumber: String(row.invoiceNumber ?? row.invoice_number ?? "").trim(),
    lines,
  };
}

function normalizeClientInvoiceExistsResponse(row) {
  if (!row || typeof row !== "object") {
    return { exists: false, invoiceId: null, invoiceNumber: "", status: "" };
  }
  return {
    exists: row.exists === true,
    invoiceId: row.invoiceId ?? row.invoice_id ?? null,
    invoiceNumber: String(row.invoiceNumber ?? row.invoice_number ?? "").trim(),
    status: String(row.status ?? "").trim(),
  };
}

function normalizeClientInvoiceDraftResponse(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const lines = Array.isArray(row.lines)
    ? row.lines.map(normalizeClientInvoiceDraftLine).filter(Boolean)
    : [];
  return {
    invoiceId: row.invoiceId ?? row.invoice_id,
    invoiceNumber: String(row.invoiceNumber ?? row.invoice_number ?? "").trim(),
    serviceMonth: String(row.serviceMonth ?? row.service_month ?? "").trim(),
    poNumber: String(row.poNumber ?? row.po_number ?? "").trim(),
    status: String(row.status ?? "").trim(),
    invProfileId: row.invProfileId ?? row.inv_profile_id,
    custgroup: String(row.custgroup ?? "").trim(),
    custId: row.custId ?? row.custid ?? null,
    invoiceProgramName: String(row.invoiceProgramName ?? row.invoice_program_name ?? "").trim(),
    companyNumber: String(row.companyNumber ?? row.company_number ?? "").trim(),
    adjustments: row.adjustments != null ? Number(row.adjustments) : 0,
    lines,
  };
}

function normalizeMonthlyInvoiceGenerationResponse(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  return {
    profilesCreated: Number(row.profilesCreated ?? row.profiles_created ?? 0),
    profilesSkipped: Number(row.profilesSkipped ?? row.profiles_skipped ?? 0),
    profilesFailed: Number(row.profilesFailed ?? row.profiles_failed ?? 0),
    serviceMonth: String(row.serviceMonth ?? row.service_month ?? "").trim(),
    billingCycle: Number(row.billingCycle ?? row.billing_cycle ?? 0),
    runDate: String(row.runDate ?? row.run_date ?? "").trim(),
    message: String(row.message ?? "").trim(),
  };
}

function normalizeLaborOpCodeHoursResult(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const laborHoursRaw = row.laborHours ?? row.labor_hours ?? row.labor_hour;
  const laborHours =
    laborHoursRaw != null && laborHoursRaw !== "" ? Number(laborHoursRaw) : null;
  return {
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
    opCode: String(row.opCode ?? row.op_code ?? "").trim(),
    laborHours: Number.isFinite(laborHours) ? laborHours : null,
  };
}

function normalizeLaborJacketHoursTotalResponse(row) {
  if (!row || typeof row !== "object") {
    return { totalLaborHours: null };
  }
  const totalRaw = row.totalLaborHours ?? row.total_labor_hours;
  const totalLaborHours =
    totalRaw != null && totalRaw !== "" ? Number(totalRaw) : null;
  return {
    totalLaborHours: Number.isFinite(totalLaborHours) ? totalLaborHours : null,
  };
}

function normalizeOptionalNumber(value) {
  if (value == null || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeClientInvoicePcrOpCodeEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const opCode = String(entry.opCode ?? entry.op_code ?? "").trim();
  if (!opCode) {
    return null;
  }
  return {
    opCode,
    hourQty: normalizeOptionalNumber(entry.hourQty ?? entry.hour_qty),
    opCodeCost: normalizeOptionalNumber(entry.opCodeCost ?? entry.op_code_cost),
  };
}

function normalizePcrPartialBillHistoryEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  return {
    pcrPartialBilledId: normalizeOptionalNumber(
      entry.pcrPartialBilledId ?? entry.pcr_partial_billed_id
    ),
    partialMonth: normalizeOptionalNumber(entry.partialMonth ?? entry.partial_month),
    billingMonth: String(entry.billingMonth ?? entry.billing_month ?? "").trim(),
    monthLabel: String(entry.monthLabel ?? entry.month_label ?? "").trim(),
    amount: normalizeOptionalNumber(entry.amount),
  };
}

function normalizePcrPartialBillHistory(row) {
  if (!row || typeof row !== "object") {
    return { pcr: "", billedRows: [], totalBilled: null };
  }
  const rawRows = Array.isArray(row.billedRows)
    ? row.billedRows
    : Array.isArray(row.billed_rows)
      ? row.billed_rows
      : [];
  return {
    pcr: String(row.pcr ?? "").trim(),
    billedRows: rawRows.map(normalizePcrPartialBillHistoryEntry).filter(Boolean),
    totalBilled: normalizeOptionalNumber(row.totalBilled ?? row.total_billed),
  };
}

function normalizePostagePartialBillHistoryEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  return {
    postagePartialBilledId: normalizeOptionalNumber(
      entry.postagePartialBilledId ?? entry.postage_partial_billed_id
    ),
    partialMonth: normalizeOptionalNumber(entry.partialMonth ?? entry.partial_month),
    billingMonth: String(entry.billingMonth ?? entry.billing_month ?? "").trim(),
    monthLabel: String(entry.monthLabel ?? entry.month_label ?? "").trim(),
    amount: normalizeOptionalNumber(entry.amount),
  };
}

function normalizePostagePartialBillHistory(row) {
  if (!row || typeof row !== "object") {
    return { invoicePostageId: null, billedRows: [], totalBilled: null };
  }
  const rawRows = Array.isArray(row.billedRows)
    ? row.billedRows
    : Array.isArray(row.billed_rows)
      ? row.billed_rows
      : [];
  return {
    invoicePostageId: normalizeOptionalNumber(row.invoicePostageId ?? row.invoice_postage_id),
    billedRows: rawRows.map(normalizePostagePartialBillHistoryEntry).filter(Boolean),
    totalBilled: normalizeOptionalNumber(row.totalBilled ?? row.total_billed),
  };
}

function normalizeClientInvoicePostageSubAccountEntry(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  return {
    invoicePostageSubAccountId: normalizeOptionalNumber(
      row.invoicePostageSubAccountId ?? row.invoice_postage_sub_account_id
    ),
    subAccountName: String(row.subAccountName ?? row.sub_account_name ?? "").trim(),
    postageQuantity: normalizeOptionalNumber(row.postageQuantity ?? row.postage_quantity),
    postageAmount: normalizeOptionalNumber(row.postageAmount ?? row.postage_amount),
  };
}

function normalizeClientInvoicePostageResult(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const rawSubAccounts = Array.isArray(row.subAccounts)
    ? row.subAccounts
    : Array.isArray(row.sub_accounts)
      ? row.sub_accounts
      : [];
  return {
    invoicePostageId: normalizeOptionalNumber(row.invoicePostageId ?? row.invoice_postage_id),
    invProfileDetailId: normalizeOptionalNumber(
      row.invProfileDetailId ?? row.inv_profile_detail_id
    ),
    serviceCode: String(row.serviceCode ?? row.service_code ?? "").trim(),
    custId: normalizeOptionalNumber(row.custId ?? row.cust_id ?? row.custid),
    companyNumber: String(row.companyNumber ?? row.company_number ?? "").trim(),
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
    tranMonth: String(row.tranMonth ?? row.tran_month ?? "").trim(),
    tranYear: String(row.tranYear ?? row.tran_year ?? "").trim(),
    totalAmount: normalizeOptionalNumber(row.totalAmount ?? row.total_amount),
    finalStatus:
      row.finalStatus === true ||
      row.final_status === true ||
      String(row.finalStatus ?? row.final_status ?? "")
        .trim()
        .toUpperCase() === "Y",
    finalStatusDate: String(row.finalStatusDate ?? row.final_status_date ?? "").trim(),
    finalStatusEmployee: normalizeOptionalNumber(
      row.finalStatusEmployee ?? row.final_status_employee
    ),
    subAccounts: rawSubAccounts.map(normalizeClientInvoicePostageSubAccountEntry).filter(Boolean),
  };
}

function normalizePostageInvoiceLineLink(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const invoiceLineId = Number(row.invoiceLineId ?? row.invoice_line_id);
  const invoicePostageId = Number(row.invoicePostageId ?? row.invoice_postage_id);
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  if (!Number.isFinite(invoicePostageId) || invoicePostageId <= 0) {
    return null;
  }
  return {
    invoiceLineId,
    invoicePostageId,
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
  };
}

function normalizeFreightPartialBillHistoryEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  return {
    freightPartialBilledId: normalizeOptionalNumber(
      entry.freightPartialBilledId ?? entry.freight_partial_billed_id
    ),
    partialMonth: normalizeOptionalNumber(entry.partialMonth ?? entry.partial_month),
    billingMonth: String(entry.billingMonth ?? entry.billing_month ?? "").trim(),
    monthLabel: String(entry.monthLabel ?? entry.month_label ?? "").trim(),
    amount: normalizeOptionalNumber(entry.amount),
  };
}

function normalizeFreightPartialBillHistory(row) {
  if (!row || typeof row !== "object") {
    return { invoiceFreightId: null, billedRows: [], totalBilled: null };
  }
  const rawRows = Array.isArray(row.billedRows)
    ? row.billedRows
    : Array.isArray(row.billed_rows)
      ? row.billed_rows
      : [];
  return {
    invoiceFreightId: normalizeOptionalNumber(row.invoiceFreightId ?? row.invoice_freight_id),
    billedRows: rawRows.map(normalizeFreightPartialBillHistoryEntry).filter(Boolean),
    totalBilled: normalizeOptionalNumber(row.totalBilled ?? row.total_billed),
  };
}

function normalizeClientInvoiceFreightDetailEntry(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  return {
    invoiceFreightDetailId: normalizeOptionalNumber(
      row.invoiceFreightDetailId ?? row.invoice_freight_detail_id
    ),
    freightType: String(row.freightType ?? row.freight_type ?? "").trim(),
    freightOrderCount: normalizeOptionalNumber(
      row.freightOrderCount ?? row.freight_order_count
    ),
    freightAmount: normalizeOptionalNumber(row.freightAmount ?? row.freight_amount),
  };
}

function normalizeClientInvoiceFreightResult(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const rawFreightDetails = Array.isArray(row.freightDetails)
    ? row.freightDetails
    : Array.isArray(row.freight_details)
      ? row.freight_details
      : [];
  return {
    invoiceFreightId: normalizeOptionalNumber(row.invoiceFreightId ?? row.invoice_freight_id),
    invProfileDetailId: normalizeOptionalNumber(
      row.invProfileDetailId ?? row.inv_profile_detail_id
    ),
    serviceCode: String(row.serviceCode ?? row.service_code ?? "").trim(),
    custId: normalizeOptionalNumber(row.custId ?? row.cust_id ?? row.custid),
    companyNumber: String(row.companyNumber ?? row.company_number ?? "").trim(),
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
    tranMonth: String(row.tranMonth ?? row.tran_month ?? "").trim(),
    tranYear: String(row.tranYear ?? row.tran_year ?? "").trim(),
    fedexTotalAmount: normalizeOptionalNumber(
      row.fedexTotalAmount ?? row.fedex_total_amount
    ),
    upsTotalAmount: normalizeOptionalNumber(row.upsTotalAmount ?? row.ups_total_amount),
    finalStatus:
      row.finalStatus === true ||
      row.final_status === true ||
      String(row.finalStatus ?? row.final_status ?? "")
        .trim()
        .toUpperCase() === "Y",
    finalStatusDate: String(row.finalStatusDate ?? row.final_status_date ?? "").trim(),
    finalStatusEmployee: normalizeOptionalNumber(
      row.finalStatusEmployee ?? row.final_status_employee
    ),
    freightDetails: rawFreightDetails.map(normalizeClientInvoiceFreightDetailEntry).filter(Boolean),
  };
}

function normalizeFreightInvoiceLineLink(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const invoiceLineId = Number(row.invoiceLineId ?? row.invoice_line_id);
  const invoiceFreightId = Number(row.invoiceFreightId ?? row.invoice_freight_id);
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  if (!Number.isFinite(invoiceFreightId) || invoiceFreightId <= 0) {
    return null;
  }
  return {
    invoiceLineId,
    invoiceFreightId,
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
  };
}

function normalizeClientInvoicePodDetailEntry(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  return {
    invoicePodDetailId: normalizeOptionalNumber(
      row.invoicePodDetailId ?? row.invoice_pod_detail_id
    ),
    totalPodCount: normalizeOptionalNumber(row.totalPodCount ?? row.total_pod_count),
    totalPodAmount: normalizeOptionalNumber(row.totalPodAmount ?? row.total_pod_amount),
  };
}

function normalizeClientInvoicePodResult(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const rawPodDetails = Array.isArray(row.podDetails)
    ? row.podDetails
    : Array.isArray(row.pod_details)
      ? row.pod_details
      : [];
  return {
    invoicePodId: normalizeOptionalNumber(row.invoicePodId ?? row.invoice_pod_id),
    invProfileDetailId: normalizeOptionalNumber(
      row.invProfileDetailId ?? row.inv_profile_detail_id
    ),
    serviceCode: String(row.serviceCode ?? row.service_code ?? "").trim(),
    custId: normalizeOptionalNumber(row.custId ?? row.cust_id ?? row.custid),
    companyNumber: String(row.companyNumber ?? row.company_number ?? "").trim(),
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
    tranMonth: String(row.tranMonth ?? row.tran_month ?? "").trim(),
    tranYear: String(row.tranYear ?? row.tran_year ?? "").trim(),
    totalPodCount: normalizeOptionalNumber(row.totalPodCount ?? row.total_pod_count),
    totalAmount: normalizeOptionalNumber(row.totalAmount ?? row.total_amount),
    finalStatus:
      row.finalStatus === true ||
      row.final_status === true ||
      String(row.finalStatus ?? row.final_status ?? "")
        .trim()
        .toUpperCase() === "Y",
    finalStatusDate: String(row.finalStatusDate ?? row.final_status_date ?? "").trim(),
    finalStatusEmployee: normalizeOptionalNumber(
      row.finalStatusEmployee ?? row.final_status_employee
    ),
    podDetails: rawPodDetails.map(normalizeClientInvoicePodDetailEntry).filter(Boolean),
  };
}

function normalizePodInvoiceLineLink(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const invoiceLineId = Number(row.invoiceLineId ?? row.invoice_line_id);
  const invoicePodId = Number(row.invoicePodId ?? row.invoice_pod_id);
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  if (!Number.isFinite(invoicePodId) || invoicePodId <= 0) {
    return null;
  }
  return {
    invoiceLineId,
    invoicePodId,
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
  };
}

function normalizeClientInvoicePcrResult(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const rawOpCodes = Array.isArray(row.opCodes)
    ? row.opCodes
    : Array.isArray(row.op_codes)
      ? row.op_codes
      : [];
  return {
    invoicePcrId: normalizeOptionalNumber(row.invoicePcrId ?? row.invoice_pcr_id),
    invProfileDetailId: normalizeOptionalNumber(
      row.invProfileDetailId ?? row.inv_profile_detail_id
    ),
    serviceCode: String(row.serviceCode ?? row.service_code ?? "").trim(),
    custId: normalizeOptionalNumber(row.custId ?? row.cust_id ?? row.custid),
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
    pcrType: String(row.pcrType ?? row.pcr_type ?? "").trim(),
    pcr: String(row.pcr ?? "").trim(),
    pcrTitle: String(row.pcrTitle ?? row.pcr_title ?? "").trim(),
    estimateApproved: normalizeOptionalNumber(
      row.estimateApproved ?? row.estimate_approved
    ),
    clientApprovedAmount: normalizeOptionalNumber(
      row.clientApprovedAmount ?? row.client_approved_amount
    ),
    actualCost: normalizeOptionalNumber(row.actualCost ?? row.actual_cost),
    jcnCount: normalizeOptionalNumber(row.jcnCount ?? row.jcn_count),
    jcnCost: normalizeOptionalNumber(row.jcnCost ?? row.jcn_cost),
    lastClosedDate: String(row.lastClosedDate ?? row.last_closed_date ?? "").trim(),
    mtpDate: String(row.mtpDate ?? row.mtp_date ?? "").trim(),
    finalStatus:
      row.finalStatus === true ||
      row.final_status === true ||
      String(row.finalStatus ?? row.final_status ?? "")
        .trim()
        .toUpperCase() === "Y",
    opCodes: rawOpCodes.map(normalizeClientInvoicePcrOpCodeEntry).filter(Boolean),
  };
}

function normalizePcrInvoiceLineLink(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const invoiceLineId = Number(row.invoiceLineId ?? row.invoice_line_id);
  const invoicePcrId = Number(row.invoicePcrId ?? row.invoice_pcr_id);
  if (!Number.isFinite(invoiceLineId) || invoiceLineId <= 0) {
    return null;
  }
  if (!Number.isFinite(invoicePcrId) || invoicePcrId <= 0) {
    return null;
  }
  return {
    invoiceLineId,
    invoicePcrId,
    jobNo: String(row.jobNo ?? row.job_no ?? "").trim(),
  };
}

export function formatBillingApiError(err) {
  const data = err?.data;
  if (data?.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .map(([field, msg]) => `${field}: ${msg}`)
      .join("\n");
  }
  if (typeof data?.detail === "string" && data.detail) {
    return data.detail;
  }
  return "Save failed. Please try again.";
}

export const billingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listClientInvoices: build.query({
      query: () => ({
        url: "/api/v1/billing/client-invoices",
        method: "GET",
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeClientInvoiceListItem).filter(Boolean)
          : [],
      providesTags: [{ type: "ClientInvoiceList", id: "LIST" }],
    }),
    getDraftClientInvoice: build.query({
      query: ({ custgroup, invoiceProgramName, companyNumber }) => ({
        url: "/api/v1/billing/client-invoices/draft",
        method: "GET",
        params: {
          custgroup,
          invoiceProgramName,
          ...(companyNumber ? { companyNumber } : {}),
        },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftResponse(response),
      providesTags: (_result, _error, arg) =>
        arg?.custgroup && arg?.invoiceProgramName
          ? [
              {
                type: "ClientInvoiceDraft",
                id: `${arg.custgroup}:${arg.invoiceProgramName}:${arg.companyNumber ?? ""}`,
              },
            ]
          : [],
    }),
    checkClientInvoiceExists: build.query({
      query: ({
        custgroup,
        invoiceProgramName,
        serviceMonth,
        companyNumber,
        excludeInvoiceId,
      }) => ({
        url: "/api/v1/billing/client-invoices/exists",
        method: "GET",
        params: {
          custgroup,
          invoiceProgramName,
          serviceMonth,
          ...(companyNumber ? { companyNumber } : {}),
          ...(excludeInvoiceId != null ? { excludeInvoiceId } : {}),
        },
      }),
      transformResponse: (response) => normalizeClientInvoiceExistsResponse(response),
    }),
    getDraftClientInvoiceById: build.query({
      query: (invoiceId) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}`,
        method: "GET",
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftResponse(response),
      providesTags: (_result, _error, invoiceId) =>
        invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : [],
    }),
    saveDraftClientInvoice: build.mutation({
      query: (body) => ({
        url: "/api/v1/billing/client-invoices/draft",
        method: "POST",
        body,
      }),
      transformResponse: normalizeClientInvoiceDraftSaveResponse,
      invalidatesTags: (_result, _error, body) => {
        const tags = [];
        if (body?.custgroup && body?.invoiceProgramName) {
          tags.push({
            type: "ClientInvoiceDraft",
            id: `${body.custgroup}:${body.invoiceProgramName}:${body.companyNumber ?? ""}`,
          });
        }
        const invoiceId = body?.invoiceId ?? _result?.invoiceId;
        if (invoiceId != null) {
          tags.push({ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` });
        }
        tags.push({ type: "ClientInvoiceList", id: "LIST" });
        return tags.length > 0 ? tags : [{ type: "ClientInvoiceDraft" }];
      },
    }),
    updateClientInvoiceLineSuppressed: build.mutation({
      query: ({ invoiceId, invoiceLineId, suppressed }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/lines/${invoiceLineId}/suppressed`,
        method: "PATCH",
        body: { suppressed },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    updateClientInvoiceLineOverride: build.mutation({
      query: ({ invoiceId, invoiceLineId, overrideAmount, overrideReason }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/lines/${invoiceLineId}/override`,
        method: "PATCH",
        body: { overrideAmount, overrideReason },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    deleteClientInvoiceLineOverride: build.mutation({
      query: ({ invoiceId, invoiceLineId }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/lines/${invoiceLineId}/override/clear`,
        method: "PATCH",
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    updateClientInvoiceLineQuantityOverride: build.mutation({
      query: ({ invoiceId, invoiceLineId, overrideQuantity, overrideQuantityReason }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/lines/${invoiceLineId}/quantity-override`,
        method: "PATCH",
        body: { overrideQuantity, overrideQuantityReason },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    deleteClientInvoiceLineQuantityOverride: build.mutation({
      query: ({ invoiceId, invoiceLineId }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/lines/${invoiceLineId}/quantity-override/clear`,
        method: "PATCH",
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    addPcrToClientInvoice: build.mutation({
      query: ({ invoiceId, invoicePcrId, rate, method, defaultInvoiceDescription }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pcr-add-to-invoice`,
        method: "POST",
        body: {
          invoicePcrId,
          rate,
          ...(method ? { method } : {}),
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoicePcrLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    removePcrFromClientInvoice: build.mutation({
      query: ({ invoiceId, invoicePcrId, invoiceLineId }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pcr-remove-from-invoice`,
        method: "POST",
        body: {
          invoicePcrId,
          invoiceLineId,
        },
      }),
      transformResponse: (response) => ({
        pcr: normalizeClientInvoicePcrResult(response?.pcr),
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoicePcrLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    getPcrAddToInvoiceLinks: build.query({
      query: (invoiceId) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pcr-add-to-invoice-links`,
        method: "GET",
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizePcrInvoiceLineLink).filter(Boolean)
          : [],
      providesTags: (_result, _error, invoiceId) =>
        invoiceId != null
          ? [{ type: "ClientInvoicePcrLinks", id: `invoice:${invoiceId}` }]
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    markPcrNotBillable: build.mutation({
      query: ({ invoiceId, invoicePcrId, notes }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pcr-not-billable`,
        method: "POST",
        body: {
          invoicePcrId,
          notes: String(notes ?? "").trim(),
          nonBillableComments: String(notes ?? "").trim(),
        },
      }),
    }),
    getPcrPartialBillHistory: build.query({
      query: ({ pcr }) => ({
        url: "/api/v1/billing/client-invoice-pcr-partial-bill-history",
        method: "POST",
        body: {
          pcr: String(pcr ?? "").trim(),
        },
      }),
      transformResponse: (response) => normalizePcrPartialBillHistory(response),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    partialBillPcr: build.mutation({
      query: ({ invoiceId, invoicePcrId, rate, billingMonth, finalStatus, defaultInvoiceDescription }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pcr-partial-bill`,
        method: "POST",
        body: {
          invoicePcrId,
          rate,
          billingMonth: String(billingMonth ?? "").trim(),
          finalStatus: Boolean(finalStatus),
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== "object") {
          return null;
        }
        return {
          line: normalizeClientInvoiceDraftLine(response.line),
          finalStatus: Boolean(response.finalStatus ?? response.final_status),
          history: normalizePcrPartialBillHistory(response.history),
        };
      },
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    getPostagePartialBillHistory: build.query({
      query: ({ invoicePostageId }) => ({
        url: "/api/v1/billing/client-invoice-postage-partial-bill-history",
        method: "POST",
        body: {
          invoicePostageId,
        },
      }),
      transformResponse: (response) => normalizePostagePartialBillHistory(response),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    partialBillPostage: build.mutation({
      query: ({
        invoiceId,
        invoicePostageId,
        rate,
        billingMonth,
        finalStatus,
        defaultInvoiceDescription,
      }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/postage-partial-bill`,
        method: "POST",
        body: {
          invoicePostageId,
          rate,
          billingMonth: String(billingMonth ?? "").trim(),
          finalStatus: Boolean(finalStatus),
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== "object") {
          return null;
        }
        return {
          line: normalizeClientInvoiceDraftLine(response.line),
          finalStatus: Boolean(response.finalStatus ?? response.final_status),
          history: normalizePostagePartialBillHistory(response.history),
        };
      },
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    getClientInvoicePcrs: build.query({
      query: ({ custId, jobNos }) => ({
        url: "/api/v1/billing/client-invoice-pcrs",
        method: "POST",
        body: {
          custId,
          jobNos: Array.isArray(jobNos) ? jobNos : [],
        },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeClientInvoicePcrResult).filter(Boolean)
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getClientInvoicePostage: build.query({
      query: ({ custId, jobNos, serviceMonth }) => ({
        url: "/api/v1/billing/client-invoice-postage",
        method: "POST",
        body: {
          custId,
          jobNos: Array.isArray(jobNos) ? jobNos : [],
          ...(serviceMonth ? { serviceMonth: String(serviceMonth).trim() } : {}),
        },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeClientInvoicePostageResult).filter(Boolean)
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    addPostageToClientInvoice: build.mutation({
      query: ({ invoiceId, invoicePostageId, rate, defaultInvoiceDescription }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/postage-add-to-invoice`,
        method: "POST",
        body: {
          invoicePostageId,
          rate,
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoicePostageLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    removePostageFromClientInvoice: build.mutation({
      query: ({ invoiceId, invoicePostageId, invoiceLineId }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/postage-remove-from-invoice`,
        method: "POST",
        body: {
          invoicePostageId,
          invoiceLineId,
        },
      }),
      transformResponse: (response) => ({
        postage: normalizeClientInvoicePostageResult(response?.postage),
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoicePostageLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    getPostageAddToInvoiceLinks: build.query({
      query: (invoiceId) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/postage-add-to-invoice-links`,
        method: "GET",
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizePostageInvoiceLineLink).filter(Boolean)
          : [],
      providesTags: (_result, _error, invoiceId) =>
        invoiceId != null
          ? [{ type: "ClientInvoicePostageLinks", id: `invoice:${invoiceId}` }]
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    markPostageNotBillable: build.mutation({
      query: ({ invoiceId, invoicePostageId, notes }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/postage-not-billable`,
        method: "POST",
        body: {
          invoicePostageId,
          notes: String(notes ?? "").trim(),
          nonBillableComments: String(notes ?? "").trim(),
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) =>
        invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : [],
    }),
    getFreightPartialBillHistory: build.query({
      query: ({ invoiceFreightId }) => ({
        url: "/api/v1/billing/client-invoice-freight-partial-bill-history",
        method: "POST",
        body: {
          invoiceFreightId,
        },
      }),
      transformResponse: (response) => normalizeFreightPartialBillHistory(response),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    partialBillFreight: build.mutation({
      query: ({
        invoiceId,
        invoiceFreightId,
        rate,
        billingMonth,
        finalStatus,
        billFedex,
        billUps,
        defaultInvoiceDescription,
      }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/freight-partial-bill`,
        method: "POST",
        body: {
          invoiceFreightId,
          rate,
          billingMonth: String(billingMonth ?? "").trim(),
          finalStatus: Boolean(finalStatus),
          billFedex: Boolean(billFedex),
          billUps: Boolean(billUps),
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== "object") {
          return null;
        }
        return {
          line: normalizeClientInvoiceDraftLine(response.line),
          finalStatus: Boolean(response.finalStatus ?? response.final_status),
          history: normalizeFreightPartialBillHistory(response.history),
        };
      },
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : []),
      ],
    }),
    getClientInvoiceFreight: build.query({
      query: ({ custId, jobNos, serviceMonth }) => ({
        url: "/api/v1/billing/client-invoice-freight",
        method: "POST",
        body: {
          custId,
          jobNos: Array.isArray(jobNos) ? jobNos : [],
          ...(serviceMonth ? { serviceMonth: String(serviceMonth).trim() } : {}),
        },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeClientInvoiceFreightResult).filter(Boolean)
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    addFreightToClientInvoice: build.mutation({
      query: ({ invoiceId, invoiceFreightId, rate, defaultInvoiceDescription }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/freight-add-to-invoice`,
        method: "POST",
        body: {
          invoiceFreightId,
          rate,
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoiceFreightLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    removeFreightFromClientInvoice: build.mutation({
      query: ({ invoiceId, invoiceFreightId, invoiceLineId }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/freight-remove-from-invoice`,
        method: "POST",
        body: {
          invoiceFreightId,
          invoiceLineId,
        },
      }),
      transformResponse: (response) => ({
        freight: normalizeClientInvoiceFreightResult(response?.freight),
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoiceFreightLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    getFreightAddToInvoiceLinks: build.query({
      query: (invoiceId) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/freight-add-to-invoice-links`,
        method: "GET",
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeFreightInvoiceLineLink).filter(Boolean)
          : [],
      providesTags: (_result, _error, invoiceId) =>
        invoiceId != null
          ? [{ type: "ClientInvoiceFreightLinks", id: `invoice:${invoiceId}` }]
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    markFreightNotBillable: build.mutation({
      query: ({ invoiceId, invoiceFreightId, notes }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/freight-not-billable`,
        method: "POST",
        body: {
          invoiceFreightId,
          notes: String(notes ?? "").trim(),
          nonBillableComments: String(notes ?? "").trim(),
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) =>
        invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : [],
    }),
    getClientInvoicePod: build.query({
      query: ({ custId, jobNos, serviceMonth }) => ({
        url: "/api/v1/billing/client-invoice-pod",
        method: "POST",
        body: {
          custId,
          jobNos: Array.isArray(jobNos) ? jobNos : [],
          ...(serviceMonth ? { serviceMonth: String(serviceMonth).trim() } : {}),
        },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeClientInvoicePodResult).filter(Boolean)
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    addPodToClientInvoice: build.mutation({
      query: ({ invoiceId, invoicePodId, rate, defaultInvoiceDescription }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pod-add-to-invoice`,
        method: "POST",
        body: {
          invoicePodId,
          rate,
          ...(defaultInvoiceDescription
            ? { defaultInvoiceDescription: String(defaultInvoiceDescription).trim() }
            : {}),
        },
      }),
      transformResponse: (response) => normalizeClientInvoiceDraftLine(response),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoicePodLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    removePodFromClientInvoice: build.mutation({
      query: ({ invoiceId, invoicePodId, invoiceLineId }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pod-remove-from-invoice`,
        method: "POST",
        body: {
          invoicePodId,
          invoiceLineId,
        },
      }),
      transformResponse: (response) => ({
        pod: normalizeClientInvoicePodResult(response?.pod),
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "ClientInvoiceList", id: "LIST" },
        ...(invoiceId != null
          ? [
              { type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` },
              { type: "ClientInvoicePodLinks", id: `invoice:${invoiceId}` },
            ]
          : []),
      ],
    }),
    getPodAddToInvoiceLinks: build.query({
      query: (invoiceId) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pod-add-to-invoice-links`,
        method: "GET",
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizePodInvoiceLineLink).filter(Boolean)
          : [],
      providesTags: (_result, _error, invoiceId) =>
        invoiceId != null
          ? [{ type: "ClientInvoicePodLinks", id: `invoice:${invoiceId}` }]
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    markPodNotBillable: build.mutation({
      query: ({ invoiceId, invoicePodId, notes }) => ({
        url: `/api/v1/billing/client-invoices/draft/${invoiceId}/pod-not-billable`,
        method: "POST",
        body: {
          invoicePodId,
          notes: String(notes ?? "").trim(),
          nonBillableComments: String(notes ?? "").trim(),
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) =>
        invoiceId != null
          ? [{ type: "ClientInvoiceDraft", id: `invoice:${invoiceId}` }]
          : [],
    }),
    getLaborOpCodeHours: build.query({
      query: ({ serviceMonth, entries }) => ({
        url: "/api/v1/billing/labor-op-code-hours",
        method: "POST",
        body: {
          serviceMonth,
          entries: Array.isArray(entries) ? entries : [],
        },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeLaborOpCodeHoursResult).filter(Boolean)
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getLaborJacketHoursTotal: build.query({
      query: ({ serviceMonth, jobNos }) => ({
        url: "/api/v1/billing/labor-jacket-hours-total",
        method: "POST",
        body: {
          serviceMonth,
          jobNos: Array.isArray(jobNos) ? jobNos : [],
        },
      }),
      transformResponse: (response) => normalizeLaborJacketHoursTotalResponse(response),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    getLaborJacketOpCodeHours: build.query({
      query: ({ serviceMonth, jobNos }) => ({
        url: "/api/v1/billing/labor-jacket-op-code-hours",
        method: "POST",
        body: {
          serviceMonth,
          jobNos: Array.isArray(jobNos) ? jobNos : [],
        },
      }),
      transformResponse: (response) =>
        Array.isArray(response)
          ? response.map(normalizeLaborOpCodeHoursResult).filter(Boolean)
          : [],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),
    runMonthlyInvoiceGeneration: build.mutation({
      query: ({ runDate } = {}) => ({
        url: "/api/v1/billing/monthly-invoices/generate",
        method: "POST",
        params: runDate ? { runDate } : {},
      }),
      transformResponse: (response) => normalizeMonthlyInvoiceGenerationResponse(response),
      invalidatesTags: [{ type: "ClientInvoiceList", id: "LIST" }],
    }),
  }),
});

export const {
  useListClientInvoicesQuery,
  useGetDraftClientInvoiceQuery,
  useCheckClientInvoiceExistsQuery,
  useGetDraftClientInvoiceByIdQuery,
  useSaveDraftClientInvoiceMutation,
  useUpdateClientInvoiceLineSuppressedMutation,
  useUpdateClientInvoiceLineOverrideMutation,
  useDeleteClientInvoiceLineOverrideMutation,
  useUpdateClientInvoiceLineQuantityOverrideMutation,
  useDeleteClientInvoiceLineQuantityOverrideMutation,
  useAddPcrToClientInvoiceMutation,
  useRemovePcrFromClientInvoiceMutation,
  useGetPcrAddToInvoiceLinksQuery,
  useMarkPcrNotBillableMutation,
  useGetPcrPartialBillHistoryQuery,
  usePartialBillPcrMutation,
  useGetPostagePartialBillHistoryQuery,
  usePartialBillPostageMutation,
  useGetClientInvoicePcrsQuery,
  useGetClientInvoicePostageQuery,
  useAddPostageToClientInvoiceMutation,
  useRemovePostageFromClientInvoiceMutation,
  useGetPostageAddToInvoiceLinksQuery,
  useMarkPostageNotBillableMutation,
  useGetFreightPartialBillHistoryQuery,
  usePartialBillFreightMutation,
  useGetClientInvoiceFreightQuery,
  useAddFreightToClientInvoiceMutation,
  useRemoveFreightFromClientInvoiceMutation,
  useGetFreightAddToInvoiceLinksQuery,
  useMarkFreightNotBillableMutation,
  useGetClientInvoicePodQuery,
  useAddPodToClientInvoiceMutation,
  useRemovePodFromClientInvoiceMutation,
  useGetPodAddToInvoiceLinksQuery,
  useMarkPodNotBillableMutation,
  useGetLaborOpCodeHoursQuery,
  useGetLaborJacketHoursTotalQuery,
  useGetLaborJacketOpCodeHoursQuery,
  useRunMonthlyInvoiceGenerationMutation,
} = billingApi;
